import { z } from 'zod';

/**
 * Wave 27 — Fornecedor catálogo.
 *
 * Per-supplier price book. Each item is optional SKU + required name +
 * unit of measure + last price (cache). The idea is: once a PM issues a
 * PO with 30 line items, they shouldn't have to retype those items the
 * next month — they pick from the catalog and hit send.
 *
 * `lastPriceCents` is a cache; the source of truth is the PO history.
 * Updating an item does NOT retroactively change POs.
 */

// A loose unit regex — construction uses "m2", "m³", "un", "vb", "kg",
// "sc", "pç", "h" etc. We cap at 8 chars and forbid whitespace so a
// pasted cell doesn't smuggle in a line break.
const unitRegex = /^[^\s]{1,8}$/u;

export const supplierCatalogItemSchema = z.object({
  id: z.string(),
  supplierId: z.string(),
  code: z.string().nullable(),
  name: z.string(),
  unit: z.string(),
  lastPriceCents: z.number().int().nonnegative(),
  notes: z.string().nullable(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SupplierCatalogItem = z.infer<typeof supplierCatalogItemSchema>;

export const createSupplierCatalogItemRequestSchema = z.object({
  code: z.string().trim().min(1).max(64).nullable().optional(),
  name: z.string().trim().min(1).max(200),
  unit: z.string().trim().regex(unitRegex, 'unidade inválida'),
  lastPriceCents: z.number().int().nonnegative().default(0),
  notes: z.string().trim().max(500).nullable().optional(),
});
export type CreateSupplierCatalogItemRequest = z.infer<
  typeof createSupplierCatalogItemRequestSchema
>;

export const updateSupplierCatalogItemRequestSchema = z.object({
  code: z.string().trim().min(1).max(64).nullable().optional(),
  name: z.string().trim().min(1).max(200).optional(),
  unit: z.string().trim().regex(unitRegex, 'unidade inválida').optional(),
  lastPriceCents: z.number().int().nonnegative().optional(),
  notes: z.string().trim().max(500).nullable().optional(),
  archived: z.boolean().optional(),
});
export type UpdateSupplierCatalogItemRequest = z.infer<
  typeof updateSupplierCatalogItemRequestSchema
>;

export const listSupplierCatalogResponseSchema = z.array(
  supplierCatalogItemSchema,
);
export type ListSupplierCatalogResponse = z.infer<
  typeof listSupplierCatalogResponseSchema
>;
